import React, { useState, useEffect } from 'react';
import { useUserManagement } from '../../hooks/useUserManagement';
import UserTable from '../../components/admin/users/UserTable';
import UserForm from '../../components/admin/users/UserForm';
import Drawer from '../../components/ui/drawer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Alert } from '../../components/ui/alert';
import { checkAdminStatus } from '../../utils/adminUtils';
import { debugUserOperations } from '../../utils/debugUtils';
import { 
  Plus, 
  Search, 
  Users as UsersIcon, 
  Filter,
  Download,
  Loader2,
  Shield
} from 'lucide-react';

const Users = () => {
  const {
    filteredUsers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    sortConfig,
    setSortConfig,
    createUser,
    updateUser,
    deleteUser,
    cloneUser
  } = useUserManagement();

  const [selectedUser, setSelectedUser] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create', 'edit', 'clone'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminStatus, setAdminStatus] = useState(null);

  // Check admin status on component mount
  useEffect(() => {
    const checkAdmin = async () => {
      const isAdmin = await checkAdminStatus();
      // const profile = await getCurrentUserProfile(); // Future: use for user context
      setAdminStatus(isAdmin);
      console.log('Admin status:', isAdmin);
      // console.log('Current user profile:', profile); // Future: enable when profile is used
    };
    
    checkAdmin();
  }, []);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleCloneUser = (user) => {
    setSelectedUser(user);
    setFormMode('clone');
    setIsFormOpen(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.user_id);
        setShowDeleteConfirm(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      console.log('Form submitted with data:', formData);
      console.log('Form mode:', formMode);
      console.log('Selected user:', selectedUser);
      
      if (formMode === 'create') {
        await createUser(formData);
      } else if (formMode === 'edit' && selectedUser) {
        console.log('Updating user:', selectedUser.user_id);
        await updateUser(selectedUser.user_id, formData);
      } else if (formMode === 'clone') {
        await cloneUser(selectedUser);
      }
      
      console.log('Form submission successful');
      setIsFormOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Form submission failed:', error);
      throw error; // Re-throw to let form handle the error state
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
  };

  const getFormTitle = () => {
    switch (formMode) {
      case 'edit': return 'Edit User';
      case 'clone': return 'Clone User';
      default: return 'Create New User';
    }
  };

  const getStats = () => {
    const total = filteredUsers.length;
    const active = filteredUsers.filter(u => u.user_subscriptions?.[0]?.status === 'active').length;
    const pro = filteredUsers.filter(u => u.user_subscriptions?.[0]?.subscription_tiers?.name === 'pro').length;
    return { total, active, pro };
  };

  const stats = getStats();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <UsersIcon className="h-8 w-8 mr-3 text-red-600" />
              User Management
              {adminStatus && (
                <Shield className="h-5 w-5 ml-2 text-green-600" title="Admin Access" />
              )}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage user accounts, subscriptions, and profiles
              {adminStatus !== null && (
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  adminStatus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {adminStatus ? 'Admin' : 'No Admin Access'}
                </span>
              )}
            </p>
          </div>
          <Button onClick={handleCreateUser} className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.active}</p>
            </div>
            <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <div className="h-4 w-4 rounded-full bg-green-600"></div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pro Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.pro}</p>
            </div>
            <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <div className="h-4 w-4 rounded-full bg-purple-600"></div>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users by name, email, username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => debugUserOperations()}
              className="bg-yellow-50 hover:bg-yellow-100"
            >
              Debug
            </Button>
          </div>
        </div>
      </Card>

      {/* Admin Status Warning */}
      {adminStatus === false && (
        <Alert variant="destructive" className="mb-6">
          <Shield className="h-4 w-4" />
          <p>
            <strong>No Admin Access:</strong> You don't have admin permissions to modify user data. 
            Contact a system administrator to grant admin access.
          </p>
        </Alert>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <p>Error loading users: {error}</p>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <span className="ml-2 text-gray-500">Loading users...</span>
        </div>
      )}

      {/* Users Table */}
      {!loading && (
        <UserTable
          users={filteredUsers}
          sortConfig={sortConfig}
          onSort={setSortConfig}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
          onClone={handleCloneUser}
        />
      )}

      {/* User Form Drawer */}
      <Drawer
        isOpen={isFormOpen}
        onClose={handleFormCancel}
        title={getFormTitle()}
        size="lg"
      >
        <UserForm
          user={selectedUser}
          mode={formMode}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </Drawer>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <Card className="p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Delete User
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>? 
              This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
              >
                Delete User
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Users;
